import React, { createRef, FC, useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { FormInstance } from 'antd/es/form/Form'
import {
    Form, Input, Space, Spin, Button, Radio, Card, RadioChangeEvent,
    Select, notification, Image as AntImage,
} from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { ConfigProvider as DslateConfigProvider, defaultConfig } from '@dslate/core'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import type { Descendant } from 'slate'
import { Text } from 'slate'
import type { DSlateRef } from '@dslate/core'
import DSlate, { DefaultPlugin } from '@dslate/dslate'
import { IAppState } from '@/store/reducers'
import { ltrim, rtrim, debounce, getUuid } from '@/utils/util'
import _ from 'lodash'
import { addVote, voteData, faceBack, getTechInfo, } from '@/services/vote.service'
import Nav from '@/components/nav'
// import { IWorkgroup } from '@/store/models/tableEdit.model'

// interface TableDataType {
//     id: string
//     techAbstract: boolean
//     techContext: string
//     techTitle?: string
//     techUrl?: Date
//     workGroup?: IWorkgroup
// }
const imgServer = process.env.REACT_APP_IMG_SERVER
const plugs = { ...DefaultPlugin }
// 删除图片工具
Reflect.deleteProperty(plugs, 'ImgPlugin')

export const handleTrim = (arr: any[]): any => {
    const _arr = _.cloneDeep(arr)
    // 第一个节点去掉左空格
    let firstNode = _arr[0]
    while ('type' in firstNode) {
        firstNode = firstNode.children[0]
    }
    firstNode.text = ltrim(firstNode.text)
    // 最后一个节点去掉右空格
    let lastNode = _arr[_arr.length - 1]
    while ('type' in lastNode) {
        lastNode = lastNode.children[lastNode.children.length - 1]
    }
    lastNode.text = rtrim(lastNode.text)
    return _arr
}
export const serialize = (children: any[]): any => {
    return children.map((node: any) => {
        if (Text.isText(node)) {
            return {
                ...node,
                text: node.text.replace(/ /g, '\xa0')
            }
        }
        return {
            ...node,
            children: serialize(node.children)
        }
    })
}

const AddVote: FC = () => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const [loaded, ] = useState(true)
    const [routeParams, setRouterParams] = useState<Record<string, string> | null>(null)
    const [voteValueType, setVoteValueType] = useState<string | number>('')
    const [voteValue, setVoteValue] = useState<string | number>('')
    const [techInfo, setTechInfo] = useState<any>(null)
    const [writeValue, setWriteValue] = useState('')
    const [imgUrl, setImgUrl] = useState('')
    const [imgSourceUrl, setImgSourceUrl] = useState('')
    const [imgLoading, setImgLoading] = useState(false)
    const [imgLoadingTips, setImgLoadingTips] = useState('正在获取原新闻内容')
    const [imgKey, setImgKey] = useState<string>('')
    const [btnLoading, setBtnLoading] = useState(false)
    const [btnDisabled, setBtnDisabled] = useState(false)
    const editorRef = useRef<DSlateRef>(null)
    const [editorKey, setEditorKey] = useState<string>('1')
    const [descriptionValue, setDescriptionValue] = useState<Descendant[]>([
        {
            type: 'paragraph',
            children: [{ text: '' }],
        },
    ])
    const formRef = createRef<FormInstance>()
    const { TextArea } = Input
    const { Option } = Select
    const layout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 18 },
    }
    const tailLayout = {
        wrapperCol: { offset: 6, span: 10 },
    }

    function isUrlValid (url: string) {
        // eslint-disable-next-line no-useless-escape
        const regex = /^((http(s)?:)\/\/)(([A-Za-z0-9-#~]+)\.)/
        return regex.test(url)
    }

    function convertImageToBase64(src: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = ''
            img.src = src
            img.onload = function () {
                const canvas = document.createElement('canvas')
                canvas.width = img.width
                canvas.height = img.height
                const ctx = canvas.getContext('2d')
                ctx?.drawImage(img, 0, 0, img.width, img.height)
                const ext = img.src.substring(img.src.lastIndexOf('.') + 1).toLowerCase()
                const dataURL = canvas.toDataURL('image/' + ext)
                resolve(dataURL)
            }
            img.onerror = function () {
                reject(new Error('Failed to load image'))
            }
        })
    }

    const getHtmlStr = (html: string | null) => {
        if (html) {
            let htmlStr = html
            if (html.includes('obj') && html.includes('str')) {
                const obj = JSON.parse(html)
                htmlStr = obj.str
            }
            const word = htmlStr?.replace(/<[^>]*>/g, '')
            return word
        } else {
            return ''
        }
    }

    const onChangeVoteType = (e: RadioChangeEvent) => {
        setVoteValueType(e.target.value)
    }

    const onChangeVote = (e: RadioChangeEvent) => {
        setVoteValue(e.target.value)
    }

    const changeWorkgroup = (val: number) => {
        console.log('changeWorkgroup: ', val)
    }
    const clearWorkgroup = () => {
        console.log('clearWorkgroup')
    }

    const onFinish = async (values: voteData) => {
        console.log('values: ', values)
        let message = t('add') + (i18n.language !== 'zh' ? ' ' : '') + t('success')
        let techContext = ''
        // = 2 时表示填入url，不需要转换富文本
        if (values.voteType === 1) {
            const editorHtmlValue = editorRef.current?.serialize({ children: serialize(handleTrim(descriptionValue)) })
            techContext = JSON.stringify({
                obj: values.techContext,
                str: editorHtmlValue
            })
        }
        setBtnLoading(true)
        if (!routeParams?.id) {
            await addVote({
                ...values,
                imageInfo: imgUrl,
                techContext,
            }).finally(() => setBtnLoading(false))
        } else {
            await faceBack({
                ...values,
                id: routeParams.id,
            }).finally(() => setBtnLoading(false))
            message =  t('vote') + (i18n.language !== 'zh' ? ' ' : '') + t('success')
        }
        
        const args = {
            message,
            description: '',
            icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
            duration: 3,
        }
        notification.open(args)
        setTimeout(() => {
            history.push('/voteReport')
        }, 1000)
    }
    const onFinishFailed = ({ values, errorFields, }: { values: voteData, errorFields: any}) => {
        console.log('onFinishFailed: ', errorFields, values)
    }

    const getCurItemInfo = async (id: string) => {
        const res = await getTechInfo({
            id
        })
        setTechInfo(res)
    }
    const onChangeUrl = async (url: any) => {
        setImgUrl('')
        if (url.startsWith('http://') || url.startsWith('https://')) {
            console.log('change url: ', url, `${imgServer}${url}`)
            setImgKey(getUuid(10))
            setBtnDisabled(true)
            setImgLoading(true)
            setImgLoadingTips('正在获取原新闻内容')
            const imgBase64 = await convertImageToBase64(`${imgServer}${url}`)
                .catch(err => {
                    console.log('convertImageToBase64 err: ', err)
                })
                .finally(() => {
                    setImgLoadingTips('加载失败')
                    setBtnDisabled(false)
                    setImgLoading(false)
                })
            setImgUrl(imgBase64 as string)
        } else {
            setBtnDisabled(false)
            setImgLoading(false)
        }
    }
    const onChangeUrlFn = useCallback(debounce(onChangeUrl, 1000),
        [voteValueType])

    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/voteReport',
            breadcrumbName: t('TMTTechBack'),
        },
        {
            path: '/addVote',
            breadcrumbName: t('publish') + (i18n.language !== 'zh' ? ' ' : '') 
                + t('techNews'),
        },
    ])
    
    useEffect(() => {
        const routeParams = new URLSearchParams(location.search)
        if (routeParams.get('id')) {
            setRoutes([
                {
                    ...routes[0],
                },
                {
                    ...routes[1],
                    breadcrumbName: 'TMT技术信息反馈'
                }
            ])
            setRouterParams({
                id: routeParams.get('id') as string,
            })
            getCurItemInfo(routeParams.get('id') as string)
        }
        const title = routeParams.get('title')
        const description = routeParams.get('description')
        if (title) {
            setVoteValueType(1)
            setEditorKey(getUuid(8))
            const techContext = [
                {
                    type: 'paragraph',
                    children: [{ text: description ? decodeURIComponent(description) : '' }],
                },
            ]
            setDescriptionValue(techContext)
            formRef.current?.setFieldsValue({
                techTitle: title,
                voteType: 1,
                techContext
            })
        }
    }, [])
    useEffect(() => {
        formRef.current?.setFieldValue('techContext', descriptionValue)
        formRef.current?.validateFields(['techContext'])
    }, [descriptionValue])

    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            {loaded
                ? (<>
                    {!routeParams?.id
                        ? <Card className="edit_table" style={{ paddingTop: 0 }}
                            title={<span>{t('publish') + (i18n.language !== 'zh' ? ' ' : '') 
                                + t('techNews')}<span style={{ fontSize: 13, color: '#aaa' }}>({t('prompt15')})</span>
                            </span>}
                        >
                            <Form {...layout} ref={formRef}
                                onFinish={onFinish}
                                onFinishFailed={onFinishFailed}
                                className="edit_table"
                            >
                                <Form.Item name="techTitle" label={t('techTitle')}
                                    rules={[{ required: true, message: t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') 
                                + t('techTitle') }]}
                                >
                                    <Input placeholder={t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') + t('techTitle')} />
                                </Form.Item>
                                <Form.Item name="voteType"
                                    label={<span className='label'>{t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                                + t('infoSource')}</span>}
                                    rules={[{ required: true, message: t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                                + t('vote') + (i18n.language !== 'zh' ? ' ' : '') + t('type') }]}
                                >
                                    <Radio.Group onChange={onChangeVoteType} value={voteValueType}>
                                        <Radio value={1}
                                            style={{ marginRight: 60, lineHeight: 1, }}
                                        >自定义输入</Radio>
                                        <Radio value={2}
                                            style={{ lineHeight: 1, }}
                                        >{t('url')}</Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {/* 摘要 */}
                                {voteValueType === 1 && <Form.Item name="techContext" label='自定义输入内容'
                                    rules={[{ required: true,
                                        validator: (_, value) => {
                                            if (!value || JSON.stringify(value) === JSON.stringify([
                                                {
                                                    type: 'paragraph',
                                                    children: [{ text: '' }],
                                                },
                                            ])) {
                                                return Promise.reject(t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') 
                                                + t('abstract'))
                                            } else {
                                                return Promise.resolve()
                                            }
                                        }
                                    }]}
                                >
                                    <Input hidden />
                                    <DslateConfigProvider
                                        value={{
                                            ...defaultConfig,
                                            plugins: [
                                                ...Object.values({
                                                    ...plugs
                                                }), 
                                            ],
                                        }}
                                    >
                                        <DSlate ref={editorRef} key={editorKey} value={descriptionValue} onChange={setDescriptionValue} />
                                    </DslateConfigProvider>
                                </Form.Item>}
                                {/* url */}
                                {voteValueType === 2 && <Form.Item name="techUrl" label={t('url')}
                                    rules={[{ required: true,
                                        validator: async (_, val) => {
                                            // 验证url合法性
                                            if (!val) {
                                                return Promise.reject(new Error(t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') 
                                        + 'URL'))
                                            }
                                            if (!isUrlValid(val)) {
                                                const helpStr = t('Please Input') + (i18n.language !== 'zh' ? ' ' : '')
                                        + t('correct') + (i18n.language !== 'zh' ? ' ' : '') + t('url')
                                                return Promise.reject(new Error(helpStr))
                                            } else {
                                                return Promise.resolve()
                                            }
                                        }
                                    }]}
                                >
                                    <Space direction='vertical' style={{ width: '100%' }}>
                                        <Input placeholder={t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') 
                                            + t('url')}
                                        onChange={(e) => {
                                            onChangeUrlFn(e.target.value)
                                            setImgSourceUrl(e.target.value)
                                        }} />
                                        {imgUrl ? <a href={imgSourceUrl} target="_blank">
                                            <div style={{ maxHeight: 500, overflow: 'auto' }}>
                                                <AntImage src={imgUrl} width='100%' preview={false}
                                                    placeholder={<Spin />} style={{ width: 1200 }} />
                                            </div>
                                            <div style={{ textAlign: 'center' }}><a>点击查看原新闻</a></div>
                                        </a> : (imgLoading && <Spin size="large" tip={imgLoadingTips}></Spin>)}
                                    </Space>
                                </Form.Item>}
                                <Form.Item name="workGroupId" label={<span className='label'>{t('Work Group')}</span>}>
                                    <Select allowClear placeholder={t('Work Group Placeholder')}
                                        onChange={changeWorkgroup}
                                        onClear={clearWorkgroup}
                                    >
                                        {
                                            workgroupList.map(item => {
                                                return <Option key={item.id} value={item.id}>{item.groupName}</Option>
                                            })
                                        }
                                    </Select>
                                </Form.Item>
                                <Form.Item {...tailLayout}>
                                    <Space size={20}>
                                        <Button type="primary" htmlType="submit" loading={btnLoading}
                                            disabled={btnDisabled}
                                        >{t('submit')}</Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Card>
                        : <Card className="edit_table" style={{ paddingTop: 0 }}
                            title='TMT技术信息反馈'
                            loading={!techInfo}
                        >
                            {techInfo && <Form {...layout} ref={formRef}
                                onFinish={onFinish}
                                onFinishFailed={onFinishFailed}
                                className="edit_table"
                            >
                                {techInfo.techTitle && <Form.Item name="techTitle" label={t('techTitle')}>
                                    { techInfo.techTitle }
                                </Form.Item>}
                                <Form.Item name="techContext" label={t('techNews') + (i18n.language !== 'zh' ? ' ' : '')
                                    + t('content')
                                }>
                                    {techInfo.techUrl
                                        ? <a href={techInfo.techUrl} target='_blank' style={{ display: 'block', width: '100%', textAlign: 'center', overflowY: 'auto' }}>
                                            {techInfo.imageInfo && <Space direction='vertical' style={{ width: '100%' }}>
                                                <div style={{ maxHeight: 500, overflow: 'auto' }}>
                                                    <AntImage src={techInfo.imageInfo} preview={false}
                                                        placeholder={<Spin />} style={{ width: 1200 }} key={imgKey}
                                                    />
                                                </div>
                                                <div style={{ textAlign: 'center' }}><a>点击查看原新闻</a></div>
                                            </Space>}
                                        </a>
                                        : <div className='desc' dangerouslySetInnerHTML={{
                                            __html: getHtmlStr(techInfo?.techContext)
                                        }}></div>
                                    }
                                </Form.Item>
                                <Form.Item name="isApproval" label={t('vote')}
                                    rules={[{ required: true, message: t('Please Select') + (i18n.language !== 'zh' ? ' ' : '')
                                + t('vote') }]}
                                >
                                    <Radio.Group onChange={onChangeVote} value={voteValue}>
                                        <Radio value={1}
                                            style={{ marginRight: 60, lineHeight: 1, fontSize: 25, color: 'green' }}
                                        ><CheckCircleOutlined style={{ verticalAlign: 'middle' }} /></Radio>
                                        <Radio value={2}
                                            style={{ lineHeight: 1, fontSize: 25, color: 'red' }}
                                        ><CloseCircleOutlined style={{ verticalAlign: 'middle' }} /></Radio>
                                    </Radio.Group>
                                </Form.Item>
                                <Form.Item name="remark" label={t('description')} >
                                    <TextArea
                                        value={writeValue}
                                        onChange={(e) => setWriteValue(e.target.value)}
                                        placeholder={t('Please Input') + (i18n.language !== 'zh' ? ' ' : '') 
                                    + t('description')}
                                        autoSize={{ minRows: 2, maxRows: 5 }}
                                        allowClear
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                                <Form.Item {...tailLayout}>
                                    <Space size={20}>
                                        <Button type="primary" htmlType="submit" loading={btnLoading}>{t('submit')}</Button>
                                    </Space>
                                </Form.Item>
                            </Form>}
                        </Card>
                    }
                </>)
                : (
                    <div 
                        style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    ><Spin size="large" /></div>
                )
            }
        </Space>
    )
}
export default AddVote
